import {
  Box,
  Center,
  Heading,
  HStack,
  Image,
  Stack,
  Text,
  useBreakpointValue,
  SimpleGrid,
  Container,
  Icon,
  Button,
  Flex,
  Highlight,
} from "@chakra-ui/react";
import { darkColor, mainColor } from "./mainPage/MainPage";
import CircleWithNumber from "./mainPage/CircleWithNumber";
import image from "../assets/images/3stepsImage.png";
import { steps_image1 } from "../assets/images/steps_image1";
import { steps_image2 } from "../assets/images/steps_image2";
import { steps_image3 } from "../assets/images/steps_image3";
import { motion } from "framer-motion";
import {
  FaCalendarAlt,
  FaUsers,
  FaInfinity,
  FaTags,
  FaEnvelopeOpenText,
  FaBriefcase,
  FaStar,
  FaWpforms,
  FaBullhorn,
  FaMoneyBill,
  FaImage,
  FaRegThumbsUp,
  FaHourglassHalf,
  FaFileInvoice,
  FaUserCircle,
} from "react-icons/fa";
import SEO from "../SEO";
import VideoSection from "./VideoSection";

const BookingSystemSection = () => {
  const displayMode = useBreakpointValue({
    base: "mobile",
    md: "tablet",
    lg: "desktop",
  });

  const ctaSize = useBreakpointValue({ base: "sm", md: "md" });

  const listItems = [
    "Online időpontfoglaló rendszer",
    "Korlátlan egyéni és csoportos szolgáltatás",
    "Korlátlan foglalás",
    "Szolgáltatás kategóriák létrehozása",
    "Automatikus email emlékeztető",
    "Várólista",
    "Számlázás (Számlázz.hu, Billingo)",
    "Előlegfizetés",
    "MyWobble ügyfélfiók",
    "Vállalkozást bemutató menü",
    "Ügyfélértékelések",
    "Szolgáltatáshoz egyedi kérdőív",
    "Marketing email küldés",
    "Referencia képek",
    "Automatikus értékelés email",
  ];

  const iconMap = [
    FaCalendarAlt,
    FaUsers,
    FaInfinity,
    FaTags,
    FaEnvelopeOpenText,
    FaHourglassHalf,
    FaFileInvoice,
    FaMoneyBill,
    FaUserCircle,
    FaBriefcase,
    FaStar,
    FaWpforms,
    FaBullhorn,
    FaImage,
    FaRegThumbsUp,
  ];

  const MotionBox = motion(Box);

  return (
    <>
      <SEO
        title="Online időpontfoglaló rendszer"
        description="Hozz létre saját foglalási oldalt, és érd el könnyedén a vállalkozásod online jelenlétét"
        keywords="weboldal készítés, online időpontfoglaló, reszponzív design, korlátlan szolgáltatás, weboldal sablon, email emlékeztető, blog írás, ügyfélértékelések"
        ogTitle="Wobble - Időpontfoglaló rendszer szolgáltatóknak"
        ogDescription="Egyszerű és gyors online időpontfoglaló rendszer egyéni- és kisvállalkozásoknak."
        ogImage="https://wobble.hu/assets/mockup_moov_studio.png"
      />

      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl" textAlign="center">
          <Heading
            as="h1"
            mb={8}
            fontSize={{ base: "3xl", md: "5xl" }}
            fontWeight="bold"
            bgGradient="linear(to-r, teal.300, blue.500)"
            bgClip="text"
          >
            Online időpontfoglaló rendszer
          </Heading>
          <Image
            justifySelf="center"
            width={600}
            src="/assets/mockup_moov_studio.png"
            sizes="(max-width: 600px) 80vw, (max-width: 1024px) 60vw, 600px"
            alt="időpontfoglaló"
            borderRadius="lg"
            mb={4}
            loading="eager"
            decoding="async"
          />
          <Box
            px={{ base: 4, md: 8 }}
            py={{ base: 10, md: 16 }}
            maxW="5xl"
            mx="auto"
            textAlign="center"
            position="relative"
          >
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              mb={8}
            >
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="gray.700"
                lineHeight="1.8"
              >
                Az{" "}
                <Highlight
                  query="online időpontfoglaló rendszer"
                  styles={{ bg: "teal.100", px: 1, borderRadius: "md" }}
                >
                  online időpontfoglaló rendszer
                </Highlight>{" "}
                megkönnyíti a vállalkozások mindennapjait. Ügyfeleid bármikor,
                akár éjjel is lefoglalhatják a számukra megfelelő időpontot, így
                nincs több felesleges telefonhívás vagy hosszadalmas egyeztetés.
                Egyszerűen kezelheted az időpontjaidat - legyen szó egyéni
                szolgáltatásról vagy csoportos eseményről.
              </Text>
            </MotionBox>

            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="gray.700"
                lineHeight="1.8"
              >
                Speciális
                <Highlight
                  query={["eszköz", "helyszín", "személy"]}
                  styles={{ bg: "teal.100", px: 1, borderRadius: "md" }}
                >
                  eszköz, helyszín vagy személy
                </Highlight>{" "}
                kiválasztása már a foglalás pillanatában – így az ügyfelek maguk
                intézik, neked pedig kevesebb adminisztrációval kell
                foglalkoznod.
              </Text>
            </MotionBox>
          </Box>

          <Box
            mx="auto"
            p={{ base: 6, md: 8 }}
            bg="white"
            borderRadius="2xl"
            boxShadow="2xl"
            border="1px solid #e2e8f0"
            mb={8}
            maxW={900}
          >
            <Text
              as="h2"
              color={mainColor}
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="bold"
              textAlign="center"
              mb={8}
            >
              Mit tud az időpontfoglaló rendszer?
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {listItems.map((item, index) => {
                const IconComponent = iconMap[index];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                  >
                    <Flex
                      align="center"
                      borderRadius="lg"
                      p={4}
                      boxShadow="md"
                      _hover={{
                        transform: "scale(1.02)",
                        boxShadow: "lg",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Icon
                        as={IconComponent}
                        color={mainColor}
                        boxSize={6}
                        mr={4}
                        flexShrink={0}
                      />
                      <Text fontSize="md" fontWeight="medium" color="gray.700">
                        {item}
                      </Text>
                    </Flex>
                  </motion.div>
                );
              })}
            </SimpleGrid>
          </Box>
          <Button
            as="a"
            href="https://wobble.hu/blog/wobble-idopontfoglalo-szemelyre-szabasa"
            target="_blank"
            rel="noopener noreferrer"
            _hover={{ bg: darkColor, textDecoration: "none" }}
            size={ctaSize}
            colorScheme="teal"
            mt={10}
            px={8}
            py={6}
            rounded="2xl"
            shadow="lg"
            fontWeight="bold"
          >
            Blog cikk a témában
          </Button>
        </Container>
      </Box>

      <VideoSection
        title="Foglalási oldal"
        subtitle="Videós útmutató"
        description="Saját foglalási oldal, ahol ügyfeleid minden információt megtalálnak, és pillanatok alatt lefoglalhatják az időpontot."
        videoUrl="https://www.youtube.com/embed/Fp95GLlS9QE"
        ctaText="Kipróbálom"
        ctaLink="https://app.wobble.hu"
      />

      <Box
        id="integration"
        p={{ base: 8, md: 8, lg: 12 }}
        position="relative"
        width="100%"
        bg="white"
        scrollBehavior="smooth"
      >
        <Box
          position="relative"
          backgroundSize="cover"
          backgroundPosition="center"
          zIndex={2}
          w="100%"
          py={8}
        >
          <Heading textAlign="center" color={mainColor} as="h2">
            Hogyan működik?
          </Heading>
          <Text color={mainColor} mb={4} fontSize={20} textAlign="center">
            Online időpontfoglalás egyszerűen, 3 lépéses beállítással
          </Text>
        </Box>
        <Box
          display={displayMode === "desktop" ? "block" : "flex"}
          flexDirection={displayMode === "tablet" ? "row" : "column"}
          alignItems="center"
          justifyContent="center"
        >
          {displayMode === "desktop" && (
            <Box
              position="relative"
              backgroundSize="cover"
              backgroundPosition="center"
              zIndex={2}
            >
              <Center>
                <Image src={image} alt="3 steps" />
              </Center>
            </Box>
          )}
          {displayMode !== "desktop" && (
            <Box p={4}>
              <Box
                display={displayMode === "tablet" ? "flex" : "block"}
                flexDirection={displayMode === "tablet" ? "row" : "column"}
                alignItems="left"
                justifyContent="left"
                mb={4}
              >
                <Box w={"70%"}>{steps_image1}</Box>
                <Stack>
                  <HStack>
                    <CircleWithNumber number={1} />
                    <Text
                      mt={displayMode !== "tablet" ? 4 : 0}
                      fontSize={displayMode === "tablet" ? 18 : 24}
                      textAlign="left"
                      width={{ base: "80%", md: "auto" }}
                    >
                      lépés: Állítsa be az elérhetőségét
                    </Text>
                  </HStack>
                  <Text
                    ml={displayMode === "tablet" ? 4 : 0}
                    mt={displayMode !== "tablet" ? 4 : 0}
                  >
                    Hozd létre az általad kínált szolgáltatásokat és állítsd be
                    az időintervallumokat. Annyi szolgáltatást adhatsz meg,
                    amennyit csak szeretnél, és a valós ütemtervedhez igazodva
                    egyedi órákat is beállíthatsz. Nyugi, rendszerünk megelőzi a
                    dupla foglalásokat!
                  </Text>
                </Stack>
              </Box>
              <Box
                display={displayMode === "tablet" ? "flex" : "block"}
                flexDirection={displayMode === "tablet" ? "row" : "column"}
                alignItems="left"
                justifyContent="left"
                mb={4}
                h="100%"
              >
                <Box w={displayMode === "tablet" ? "30%" : "50%"}>
                  {steps_image2}
                </Box>
                <Stack>
                  <HStack>
                    <CircleWithNumber number={2} />
                    <Text
                      mt={displayMode !== "tablet" ? 4 : 0}
                      fontSize={displayMode === "tablet" ? 18 : 24}
                      textAlign="left"
                      width={{ base: "80%", md: "auto" }}
                    >
                      lépés: Oszd meg a foglalási linkedet
                    </Text>
                  </HStack>
                  <Text
                    ml={displayMode === "tablet" ? 4 : 0}
                    mt={displayMode !== "tablet" ? 4 : 0}
                  >
                    Egyszerűen oszd meg a foglalási oldalad URL-jét az
                    ügyfelekkel.
                  </Text>
                </Stack>
              </Box>
              <Box
                display={displayMode === "tablet" ? "flex" : "block"}
                flexDirection={displayMode === "tablet" ? "row" : "column"}
                alignItems="left"
                justifyContent="left"
                mb={4}
              >
                <Box w={"70%"}>{steps_image3}</Box>
                <Stack>
                  <HStack>
                    <CircleWithNumber number={3} />
                    <Text
                      mt={displayMode !== "tablet" ? 4 : 0}
                      fontSize={displayMode === "tablet" ? 18 : 24}
                      textAlign="left"
                      width={{ base: "80%", md: "auto" }}
                    >
                      lépés: Élvezd a problémamentes online foglalásokat
                      éjjel-nappal (az emberek 55%-a munkaidőn kívül foglal)
                    </Text>
                  </HStack>
                  <Text
                    ml={displayMode === "tablet" ? 4 : 0}
                    mt={displayMode !== "tablet" ? 4 : 0}
                  >
                    Biztosítsd az ügyfeleknek a lehetőséget, hogy bármikor
                    foglalhassanak vagy lemondhassák az időpontokat. Rendszerünk
                    automatikusan küld e-mail emlékeztetőket, így ritkábbak
                    lesznek az elmaradt időpontok és zökkenőmentes lesz az
                    ütemterved.
                  </Text>
                </Stack>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default BookingSystemSection;
